import type { SharedFileBody } from './file-shares';
import { docViewerKind, type DocViewerKind } from './doc-viewer';

/** What the shared-file read is doing right now, as the viewer holds it. */
export type SharedFileState = { status: 'loading' } | { status: 'ready'; body: SharedFileBody } | { status: 'error' };

/** The already-localized sentences the view needs. Passed IN so this module stays pure — it must not
 *  reach i18n, which would drag the app graph into every test that calls it. */
export type SharedFileLabels = { loading: string; unavailableTitle: string; gone: string; readFailed: string };

/** Exactly what `DocViewerModal` is handed: a name, a body, and the resolved kind. */
export type SharedFileView = { name: string; content: string; kind: DocViewerKind | null };

/**
 * Turn a shared-file read into the three values the viewer renders.
 *
 * Pure, and in its own module, because every interesting decision here is one a component test in this
 * package cannot reach (`testEnvironment: 'node'`, and rendering the real viewer would drag the app
 * graph — see the Tests section of this package's CLAUDE.md, which prescribes exactly this shape). Each
 * of the three was a defect Codebot found on PR #10615:
 *
 *  - **loading is a real frame, never nothing.** The modal stack renders only its TOP entry, so a viewer
 *    that renders null while the read is in flight is a blank screen with no close control — on top of a
 *    pushed thread, or after the island card has dismissed itself.
 *  - **gone and failed are DIFFERENT answers.** The owner moving, deleting or revoking the file is
 *    ordinary and retrying would never help; a read that failed says nothing about the file and is worth
 *    trying again. Collapsing them tells someone their file is gone when it is not.
 *  - **`null` from the classifier is an ANSWER**, meaning "cannot preview this one", which the modal
 *    renders itself with the download hand-off intact. Forcing `'text'` took every kind the classifier
 *    deliberately declines — a spreadsheet, a .zip, a binary — and rendered its bytes as text.
 */
export function sharedFileView(state: SharedFileState, knownName: string | undefined, labels: SharedFileLabels): SharedFileView {
  if (state.status === 'loading') {
    // The name the opener already knew, so the frame is not anonymous while the read lands.
    return { name: knownName || labels.unavailableTitle, content: labels.loading, kind: 'text' };
  }
  if (state.status === 'error') {
    return { name: labels.unavailableTitle, content: labels.readFailed, kind: 'text' };
  }
  if (!state.body.available) {
    return { name: labels.unavailableTitle, content: labels.gone, kind: 'text' };
  }
  const { fileName, content, contentType, referenceUrl } = state.body;
  // The body the viewer will ACTUALLY render — a reference-only file carries its URL here and nothing
  // else, so classifying against `content` alone answered for a document that is not the one on screen.
  const body = content || referenceUrl || '';
  return { name: fileName || '', content: body, kind: docViewerKind(fileName || knownName, body, contentType) };
}
